import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Storage key
const TOKEN_STORAGE_KEY = 'recipe_keeper_tokens';

// Token refresh buffer (refresh 5 minutes before expiry)
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

export interface AuthTokens {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  stored_at: number;
}

export interface User {
  id: string;
  email: string;
  name?: string;
  is_admin?: boolean;
}

interface AuthState {
  tokens: AuthTokens | null;
  user: User | null;
  tokenExpiry: number | null;
}

interface AuthActions {
  setTokens: (tokens: Omit<AuthTokens, 'stored_at'>) => void;
  setUser: (user: User) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  needsRefresh: () => boolean;
  getIdToken: () => string | null;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      tokens: null,
      user: null,
      tokenExpiry: null,

      // Actions
      setTokens: tokens => {
        const storedAt = Date.now();
        const tokenExpiry = storedAt + tokens.expires_in * 1000;

        set({
          tokens: {
            ...tokens,
            stored_at: storedAt,
          },
          tokenExpiry,
        });
      },

      setUser: user => {
        set({ user });
      },

      logout: () => {
        set({
          tokens: null,
          user: null,
          tokenExpiry: null,
        });
      },

      isAuthenticated: () => {
        const { tokens, tokenExpiry } = get();

        if (!tokens?.id_token) {
          return false;
        }

        // Check if token is expired (with buffer)
        if (tokenExpiry) {
          const now = Date.now();
          if (now >= tokenExpiry - TOKEN_REFRESH_BUFFER_MS) {
            return false;
          }
        }

        return true;
      },

      needsRefresh: () => {
        const { tokens, tokenExpiry } = get();

        if (!tokens?.refresh_token || !tokenExpiry) {
          return false;
        }

        const now = Date.now();
        return now >= tokenExpiry - TOKEN_REFRESH_BUFFER_MS;
      },

      getIdToken: () => {
        const { tokens } = get();
        return tokens?.id_token || null;
      },
    }),
    {
      name: TOKEN_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        tokens: state.tokens,
        user: state.user,
        tokenExpiry: state.tokenExpiry,
      }),
    },
  ),
);
